import { FC } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import parse from 'html-react-parser'
import { PhoneAvailability } from 'components/phone-availibility/PhoneAvailability'
import { Language } from 'types/language'
import { routes } from 'utils/routes'
import { socials } from 'constants/constants'
import { Email, Facebook, Instagram, Logo } from '../assets/svg'
import StayInformed from './stay-informed/StayInformed'
import styles from './Footer.module.scss'

const Footer: FC = () => {
  // Next router
  const { locale: language } = useRouter()

  // i18n
  const { t } = useTranslation(['footer', 'lookingForHelp', 'header'])

  return (
    <footer className={styles.footer}>
      <div className={styles.top}>
        <div className="container">
          <div className={styles['logo-row']}>
            <Link href={routes(language).home}>
              <a>
                <Logo className={styles.logo} />
              </a>
            </Link>
            <div className={styles['socials-container']}>
              <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className={styles['icon-link']}>
                <Facebook viewBox="0 0 512 512" className={styles.icon} />
              </a>
              <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className={styles['icon-link']}>
                <Instagram viewBox="0 0 512 512" className={styles.icon} />
              </a>
              <a href={socials.email} target="_blank" rel="noopener noreferrer" className={styles['icon-link']}>
                <Email viewBox="0 0 512 512" className={styles.icon} />
              </a>
            </div>
          </div>
          <div className={styles.columns}>
            {/* FIRST COLUMN */}
            <div>
              <div className={styles['menu-section']}>
                <div className={styles['list-title']}>{t('lookingForHelp:needHelp', 'Hulp nodig?')}</div>
                <ul>
                  <li>
                    <a href="tel:028993300" className="phone-link">
                      {t('lookingForHelp:callNumber', 'Bel 02 899 33 00')}
                    </a>
                  </li>
                  <li>
                    <PhoneAvailability />
                  </li>
                </ul>
              </div>
              <div className={styles['menu-section']}>
                <div className={styles['list-title']}>{t('ourAddress', 'Ons adres')}</div>
                <ul>
                  <li>{parse(t('address', 'Bolt, WeWork, Botanic Tower<br />Sint-Lazaruslaan 4-10<br />1210 Brussel'))}</li>
                </ul>
              </div>
            </div>
            {/* SECOND COLUMN */}
            <div>
              <div className={styles['menu-section']}>
                <div className={styles['list-title']}>{t('usefulLinks', 'Handige links')}</div>
                <ul>
                  <li>
                    <Link href={routes(language).faqPriceIncrease}>{t('faqPriceIncrease', 'Uitzonderlijke energieprijzen')}</Link>
                  </li>
                  <li>
                    <Link href={routes(language).referral}>{t('referral', 'Overtuig een vriend')}</Link>
                  </li>
                  <li>
                    <Link href={routes(language).priceList}>{t('pricelists', 'Prijslijsten')}</Link>
                  </li>
                  {language === Language.DUTCH && (
                    <li>
                      <a
                        href="https://www.vreg.be/sites/default/files/uploads/formulieren/energieovernamedocument.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Energieovernamedocument
                      </a>
                    </li>
                  )}
                </ul>
              </div>
              <div className={styles['menu-section']}>
                <ul>
                  {language === Language.DUTCH && (
                    <li>
                      <a href="https://go.boltenergie.be/" target="_blank" rel="noopener noreferrer">
                        Bolt Go
                      </a>
                    </li>
                  )}
                  <li>
                    <a href={`https://blog.boltenergie.be/${language}/?utm_source=website&utm_medium=footer`}>{t('blog', 'Blog')}</a>
                  </li>
                  <li>
                    <a
                      href={language === Language.DUTCH ? 'https://bolt.prezly.com' : 'https://bolt.prezly.com/fr'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('press', 'Press')}
                    </a>
                  </li>
                  <li>
                    <Link href={routes(language).jobs}>{t('careers', 'Vacatures')}</Link>
                  </li>
                  <li>
                    <Link href={routes(language).projects}>{t('header:projects', 'Projecten')}</Link>
                  </li>
                </ul>
              </div>
              {language === Language.FRENCH && (
                <div className={styles['menu-section']}>
                  <div className={styles['list-title']}>Document de reprise des énergies</div>
                  <ul>
                    <li>
                      <a href="/files/document-de-reprise-des-energies.pdf" target="_blank" rel="noreferrer">
                        Sans panneaux solaires
                      </a>
                    </li>
                    <li>
                      <a href="/files/document-de-reprise-des-energies-panneaux-solaires.pdf" target="_blank" rel="noreferrer">
                        Avec panneaux solaires
                      </a>
                    </li>
                  </ul>
                </div>
              )}
            </div>
            {/* THIRD COLUMN */}
            <div>
              <div className={styles['menu-section']}>
                <div className={styles['list-title']}>{t('home:stayInformedTitle', 'Blijf op de hoogte')}</div>
                <div className="margin-bottom-l">
                  {t(
                    'home:stayInformedSubtitle',
                    'Schrijf je in voor onze nieuwsbrief en ontvang het laatste nieuws over Bolt in je mailbox!'
                  )}
                </div>
                <StayInformed />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className={styles.bottom}>
          <ul className={styles['bottom-menu']}>
            <li>
              <Link href={routes(language).terms}>{t('termsAndConditions', 'Algemene voorwaarden')}</Link>
            </li>
            <li>
              <Link href={routes(language).privacy}>{t('disclaimerPrivacyPolicy', 'Disclaimer en privacy policy')}</Link>
            </li>
          </ul>
          <div>Copyright &copy; {new Date().getFullYear()} Bolt Energie B.V.</div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
